import React from 'react'

import * as c from '../../common'
import * as r from '../../react-utils'

import Select from './Select'
import Radio from './Radio'

export type ConnectedPickerProps = c.r<{
  customerID: string
  disabled?: boolean
  field: c.MultipleChoiceField
  on: c.Surface
}>

export default React.memo<ConnectedPickerProps>(function ConnectedPicker({
  customerID,
  disabled,
  field,
  on,
}) {
  //#region global
  const [customer] = r.useCustomer(customerID)
  const closers = c.useSelector(c.selectClosers())
  const setters = c.useSelector(c.selectSetters())
  const insulationOpts = r.useInsulation(customerID)
  const windowColors = r.useWindowColors()
  const [roofLayoverOrTear] = r.useCustomerField(
    customerID,
    'roof_layover_or_tear',
  )
  const smallSystemSizes =
    r.useEnv<c.strings>('smallSystemSizes') || c.emptyArrStr
  const smallSystemSizeOpts = React.useMemo(
    (): c.Opts =>
      smallSystemSizes.map((value) => ({
        label: value,
        value,
      })),
    [smallSystemSizes],
  )
  const [batteryInstallationCompanies, batteryTypes, batterySizes] =
    r.useBattery(customerID)
  const batteryMultipleTypes =
    r.useEnv<c.Opts>('batteryMultipleTypes') || c.emptyOpts
  const { availProducts } = r.useProducts(customerID)
  const buildingTypes = r.useEnv<c.Opts>('buildingType') || c.emptyOpts
  const inverterOpts = r.useEnv<c.Opts>('inverterOpts') || c.emptyOpts
  const mpuRatings = r.useEnv<c.Opts>('mpuRatings') || c.emptyOpts
  const mainPanelScenarios =
    r.useEnv<c.Opts>('mainPanelScenarios') || c.emptyOpts
  const protectionOpts = r.useEnv<c.Opts>('protectionOpts') || c.emptyOpts
  //#endregion global
  const label = c.getFieldLabel(field, customer)
  const [, writeFieldData] = r.useCustomerField(customerID, field)
  const fieldData = customer[field]

  const [data, required] = ((): [c.Opts, boolean] => {
    if (field === 'air_conditioner_current_tons') return [c.acTons, false]
    if (field === 'air_conditioner_unit_type') return [c.acSplitUnit, false]
    if (field === 'air_conditioner_new_tons') return [c.acTons, false]
    if (field === 'attic_insulation_type') return [insulationOpts, true]
    if (field === 'battery_size') return [batterySizes, true]
    if (field === 'battery_type') return [batteryTypes, true]
    if (field === 'battery_multiple_bank_location')
      return [batteryMultipleTypes, true]
    if (field === 'buildingType') return [buildingTypes, true]
    if (field === 'homeRep') return [closers, true]
    if (field === 'homeStoryNumber') return [storyNumberOpts, true]
    if (field === 'inverter_swap_size') return [inverterOpts, true]
    if (field === 'main_panel_upgrade_needed_or_requested') {
      return [c.mainPanelUpgradeNeededOrRequested, true]
    }
    if (field === 'main_panel_upgrade_installation_company')
      return [batteryInstallationCompanies, false]
    if (field === 'main_panel_upgrade_rating') {
      return [mpuRatings, true]
    }
    if (field === 'new_windows_california_city') return [c.californiaCity, true]
    if (field === 'nirvana_panels_panel_scenario')
      return [mainPanelScenarios, true]
    if (field === 'products') return [availProducts, false]
    if (field === 'protection_which') return [protectionOpts, true]
    if (field === 'roof_layover_or_tear') return [c.roofWorkTypes, true]
    if (
      field === 'roof_layers_how_many' &&
      roofLayoverOrTear === c.RoofWorkType.Layover
    ) {
      return [c.threeLayerRoofOpts, true]
    }
    if (
      field === 'roof_layers_how_many' &&
      roofLayoverOrTear === c.RoofWorkType.ReRoofShingles
    ) {
      return [c.fourLayerRoofOpts, true]
    }
    if (
      field === 'roof_layers_how_many' &&
      roofLayoverOrTear === c.RoofWorkType.ReRoofShingles
    ) {
      return [c.fourLayerRoofOpts, true]
    }
    if (
      field === 'roof_layers_how_many' &&
      roofLayoverOrTear === c.RoofWorkType.ReRoofFlatRoof
    ) {
      return [c.threeLayerRoofOpts, true]
    }
    if (
      field === 'roof_layers_how_many' &&
      roofLayoverOrTear === c.RoofWorkType.ReRoofWoodShake
    ) {
      return [c.threeLayerRoofOpts, true]
    }
    if (field === 'roof_tear_material') {
      if (customer.roof_layover_or_tear === c.RoofWorkType.ReRoofWoodShake) {
        return [c.roofMaterialDurationsOnly30, true]
      }
      return [c.roofMaterialDurations, true]
    }
    if (field === 'roof_shingles_or_tiles') {
      return [roofShinglesOrTiles, true]
    }
    if (field === 'small_system_size') {
      return [smallSystemSizeOpts, true]
    }
    if (field === 'solarRep') return [setters, true]
    if (field === 'solarCompany') return [c.companyOptions, true]
    if (field === 'new_windows_color') return [windowColors, true]

    return [c.EMPTY_ARRAY, false]
  })()

  const shouldDisable = disabled || c.shouldDisableInput(field, customer)
  const shouldHide = c.shouldHideInput(field, customer)
  const guideline = c.getFieldGuideline(customer, field)
  const multiple = field === 'products'

  const shouldUseRadio =
    // Windows colors is 4 opts but they have short labels so it works.
    (data.length <= 3 || field === 'new_windows_color') &&
    // This is now the battery type, and it uses long labels
    field !== 'battery_multiple_bank_location' &&
    // Battery size can be 3 opts but they use long labels.
    field !== 'battery_size' &&
    field !== 'products'

  if (shouldUseRadio) {
    return (
      <Radio
        disabled={shouldDisable}
        grow={c.fieldToGrow[field]}
        guideline={guideline?.text}
        guidelineType={guideline?.type}
        hide={shouldHide}
        label={label}
        multiple={multiple}
        on={on}
        onChange={writeFieldData}
        opts={data}
        required={required}
        value={fieldData}
      />
    )
  }

  return (
    <Select
      disabled={shouldDisable}
      grow={c.fieldToGrow[field]}
      guideline={guideline?.text}
      guidelineType={guideline?.type}
      hide={shouldHide}
      inline={field === 'products'}
      label={label}
      multiple={multiple}
      on={on}
      onChange={writeFieldData}
      open={field === 'products'}
      opts={data}
      required={required}
      value={fieldData}
    />
  )
})

const storyNumberOpts: c.Opts = [
  {
    label: '1',
    value: '1',
  },
  {
    label: '2',
    value: '2',
  },
]

const roofShinglesOrTiles: c.Opts = ['Shingles', 'Tiles'].map((value) => ({
  label: value,
  value,
}))
